/**
 * Tag - 블로그 태그 전용 페이지 데이터
 *
 * 태그 문자열 자체가 slug 이며(별도 slug 필드 없음), 각 블로그 포스트의 ACF `blog.tags`
 * (콤마 구분 문자열)로 연결된다. 멤버 포스트는 이미 캐시된 블로그 리스트(S3)에서
 * `tags.includes({slug})` 인 포스트를 필터해 **캐시 생성 시점에** 로케일별 그룹
 * (`ko`/`en` — posts + relatedTags)으로 분리해 박제한다(라이브 조회 없음 — 리스트
 * 캐시에 tags 필드가 포함되어 있어야 함). 태그 페이지는 `data[locale]` 하나만 꺼내
 * 그대로 렌더한다(이후 로케일 분기 없음).
 *
 * 태그 유니버스(전체 slug + 로케일별 멤버 수)는 별도 리스트 캐시(`getTagList`)로 두어
 * generateStaticParams 가 블로그 리스트 전체 대신 이것만 읽는다. 블로그 포스트 저장 시
 * 태그별 캐시와 함께 재생성된다(content-cache-handlers).
 *
 * 연관 태그는 **선택적** `tag` 카테고리 포스트(post_name = 태그 slug) 본문의
 * `related-tags-ko: a, b, c` / `related-tags-en: x, y, z` 두 라인에서 온다(로케일별 분리).
 * 정의 포스트가 없으면 양쪽 다 빈 배열이며 이 경우에도 throw 하지 않는다(시리즈 소스와
 * 반대 — 시리즈는 소스 없으면 실패). 한 태그가 국문·영문 포스트 모두에 쓰일 때 국문
 * 페이지엔 국문 연관, 영문 페이지엔 영문 연관이 보이도록 로케일별로 나눈다.
 *
 * 태그 정의 포스트는 자체 운영 배포 버튼을 가지는 prod_deployed 대상이라, 소스(연관 태그)
 * 조회에 prodFilter 를 적용한다(시리즈와 동일). 멤버의 환경 구분은 환경별 블로그 리스트
 * 캐시(버킷)에서 온다.
 */

import { CONTENT_CATEGORY_ID } from '@/constants/content-categoryId';
import { toCmsSourceLines } from '@/utils/sanitize';
import {
  splitPostSummariesByLocale,
  type PostSummary,
} from '@/utils/post-summary';
import { parseTagList } from '@/utils/tag-slug';
import { fetchFromCache, FetchAPIOptions } from './api';
import {
  getBlogNewsPostsForList,
  type BlogNewsListItemProp,
} from './blog-news';
import { fetchFromWordpress } from './wordpress-api';
import { uploadWordpressCacheDataToS3 } from './wordpress-cache';

// ============================================
// Types
// ============================================

/** 태그 멤버 포스트 요약 — 캐시 생성 시점에 로케일별(ko/en)로 분리해 박제 */
export type TagPost = PostSummary;

/** 로케일별 연관 태그 slug */
export interface RelatedTagsByLocale {
  ko: string[];
  en: string[];
}

/** 태그 리스트 아이템 — 태그 유니버스(slug) + 로케일별 멤버 수 (generateStaticParams 용) */
export interface TagListItem {
  slug: string;
  countKo: number;
  countEn: number;
}

/** 로케일 한 쪽의 태그 페이지 데이터 — 페이지는 자기 locale 것만 꺼내 쓴다 */
export interface TagLocaleData {
  posts: TagPost[]; // 멤버 포스트 (date 내림차순)
  relatedTags: string[]; // 연관 태그 slug (정의 포스트 없으면 [])
}

/** 태그별 캐시 데이터 — 로케일별 그룹 구조 (editors-pick 캐시와 동일 패턴) */
export interface TagPageData {
  slug: string;
  ko: TagLocaleData;
  en: TagLocaleData;
}

// ============================================
// 상수
// ============================================

/** 캐시 폴더명이자 revalidateTag 태그 — content-revalidation.ts 의 tag 설정(getItemCacheTag)과 일치해야 함 */
export const getTagCacheLabel = (slug: string) => `getTag-${slug}`;

/** 태그 리스트(유니버스) 캐시 라벨 — generateStaticParams 전용(빌드 시에만 읽어 revalidateTag 대상 아님) */
export const TAG_LIST_CACHE_LABEL = 'getTagList';

// 연관 태그 정의 포스트 조회 — 태그 slug(= post_name)로 `tag` 카테고리 내에서 본문 조회.
// categoryName(slug 기반)으로 필터해 term_id 환경 차이에 영향받지 않는다(환경 중립).
// 정의 포스트가 없으면 nodes 가 비고, relatedTags 는 빈 배열이 된다.
const TAG_SOURCE_QUERY = `
query getTagSource($slugs: [String]) {
  posts(first: 1, where: { nameIn: $slugs, categoryName: "tag", status: PUBLISH }) {
    nodes {
      content
    }
  }
}`;

// ============================================
// 본문 파싱
// ============================================

/**
 * 정의 포스트 본문(HTML)에서 로케일별 연관 태그 slug 목록을 추출한다.
 * 본문은 로케일별 두 라인으로 연관 태그를 담는다:
 *   related-tags-ko: a, b, c
 *   related-tags-en: x, y, z
 * 해당 라인이 없으면 그 로케일은 빈 배열. (구 단일 `related-tags:` 포맷은 지원하지 않음)
 */
export const parseRelatedTags = (content: string): RelatedTagsByLocale => {
  const lines = toCmsSourceLines(content);

  const result: RelatedTagsByLocale = { ko: [], en: [] };
  for (const line of lines) {
    const colon = line.indexOf(':');
    if (colon === -1) continue;
    const key = line.slice(0, colon).trim().toLowerCase();
    if (key === 'related-tags-ko') {
      result.ko = parseTagList(line.slice(colon + 1));
    } else if (key === 'related-tags-en') {
      result.en = parseTagList(line.slice(colon + 1));
    }
  }

  return result;
};

// ============================================
// 캐시 데이터 빌드
// ============================================

/** 빌드 옵션 — 운영 배포된(prod_deployed) 정의 포스트만 조회할지 결정 */
type BuildOptions = Pick<FetchAPIOptions, 'prodFilter'>;

/**
 * 정의 포스트 조회 + 본문 파싱 → 연관 태그 slug 목록.
 * 정의 포스트가 없어도 throw 하지 않고 빈 배열을 반환한다(연관 태그는 선택 사항).
 * 태그 정의 포스트는 자체 운영 배포 버튼을 가지는 prod_deployed 대상이라 prodFilter 를
 * 적용한다(시리즈와 동일 — 운영 refresh 시 미배포 정의 포스트는 조회되지 않음).
 */
const fetchTagRelated = async (
  slug: string,
  options?: BuildOptions,
): Promise<RelatedTagsByLocale> => {
  const sourceData = await fetchFromWordpress(
    TAG_SOURCE_QUERY,
    { variables: { slugs: [slug] } },
    options,
  );
  const content: unknown = sourceData?.posts?.nodes?.[0]?.content;
  if (typeof content !== 'string') return { ko: [], en: [] };

  return parseRelatedTags(content);
};

/**
 * 연관 태그(정의 포스트) 조회 + 캐시된 블로그 리스트에서 멤버 필터 → TagPageData.
 *
 * 멤버는 라이브 조회 없이 이미 S3 에 캐시된 블로그 리스트(`getBlogNewsPostsForList`)에서
 * `tags.includes(slug)` 로 걸러낸다. 태그는 이 리스트를 **소비만** 한다 — 여기서 블로그
 * 리스트를 다시 만들지 않는다. 따라서 정확한 멤버를 얻으려면 블로그 리스트 캐시가
 * 최신이어야 한다(tags 필드 포함 상태로 재생성돼 있어야 함).
 * (S3 업로드 없음 — 검증/dry-run 용으로도 사용)
 */
export const buildTagData = async (
  slug: string,
  options?: BuildOptions,
): Promise<TagPageData> => {
  const [related, allPosts] = await Promise.all([
    fetchTagRelated(slug, options),
    getBlogNewsPostsForList(CONTENT_CATEGORY_ID.blog),
  ]);

  const members = allPosts.filter(post => (post.tags ?? []).includes(slug));

  // 로케일별 분리 + date 내림차순(최신순) 박제 — 저자 캐시와 공유하는 공용 규칙.
  // 페이지는 자기 locale 데이터를 그대로 렌더한다.
  const { ko, en } = splitPostSummariesByLocale(members);

  // 연관 태그는 정의 포스트 본문(로케일별)을 그대로 사용한다. "해당 로케일에 포스트가 있는
  // 태그만" 규칙은 산출(마이그레이션) 시점의 로케일별 공동출현으로 이미 보장된다 —
  // ko 포스트 집합에서 뽑은 연관 태그는 정의상 ko 포스트를 가진다.
  return {
    slug,
    ko: { posts: ko, relatedTags: related.ko },
    en: { posts: en, relatedTags: related.en },
  };
};

/**
 * 캐시된 블로그 리스트에서 태그 유니버스(slug + 로케일별 멤버 수)를 집계한다.
 * 태그 페이지 generateStaticParams 가 블로그 리스트 전체 대신 이 결과를 소비한다.
 * 멤버 0건 태그는 맵에 안 들어와 자연히 제외됨. (S3 업로드 없음)
 */
export const buildTagList = async (): Promise<TagListItem[]> => {
  const allPosts = await getBlogNewsPostsForList(CONTENT_CATEGORY_ID.blog);

  const countBySlug = new Map<string, { ko: number; en: number }>();
  for (const post of allPosts) {
    for (const tag of post.tags ?? []) {
      const entry = countBySlug.get(tag) ?? { ko: 0, en: 0 };
      if (post.lang?.includes('ko')) entry.ko += 1;
      if (post.lang?.includes('en')) entry.en += 1;
      countBySlug.set(tag, entry);
    }
  }

  return Array.from(countBySlug, ([slug, count]) => ({
    slug,
    countKo: count.ko,
    countEn: count.en,
  }));
};

/** 캐시 원소 검증 — 스키마가 어긋난 캐시가 조용히 통과하지 않도록 전 필드 검사 */
const isTagListItem = (value: unknown): value is TagListItem => {
  if (typeof value !== 'object' || value === null) return false;

  const item = value as Partial<TagListItem>;
  return (
    typeof item.slug === 'string' &&
    typeof item.countKo === 'number' &&
    typeof item.countEn === 'number'
  );
};

/**
 * 태그 리스트(유니버스) 조회
 * - refresh: true → 캐시된 블로그 리스트에서 집계 + S3 업로드 (스크립트/웹훅용)
 * - refresh: false → S3 캐시 조회 (빌드용, 캐시가 없으면 빈 배열)
 */
export const getTagList = async (
  options?: Pick<FetchAPIOptions, 'refresh'>,
): Promise<TagListItem[]> => {
  if (options?.refresh) {
    const list = await buildTagList();
    await uploadWordpressCacheDataToS3(list, TAG_LIST_CACHE_LABEL);
    return list;
  }

  const data = await fetchFromCache(TAG_LIST_CACHE_LABEL);
  return Array.isArray(data) ? data.filter(isTagListItem) : [];
};

// ============================================
// 조회 (빌드/런타임 + refresh)
// ============================================

/**
 * 태그 데이터 조회
 * - refresh: true → WordPress 조회 + 캐시 빌드 + S3 업로드 (스크립트/웹훅용, 실패 시 throw)
 * - refresh: false → S3 캐시 조회 (빌드/런타임용, 실패 시 null)
 */
export const getTag = async (
  slug: string,
  options?: FetchAPIOptions,
): Promise<TagPageData | null> => {
  const refresh = options?.refresh ?? false;
  const label = getTagCacheLabel(slug);

  if (refresh) {
    const data = await buildTagData(slug, { prodFilter: options?.prodFilter });
    await uploadWordpressCacheDataToS3(data, label);
    return data;
  }

  return fetchFromCache(label);
};

// ============================================
// 블로그 포스트 저장 커플링 — 이전∪이후 태그 재생성용
// ============================================

/**
 * 블로그 포스트(databaseId)의 현재 태그 slug 배열을 **캐시된 블로그 리스트**에서 읽는다.
 * (블로그 포스트 저장 → 소속 태그 재생성 커플링에서 "현재 값"으로 사용)
 * 커플링은 블로그 리스트 캐시가 갱신된 직후에 돌므로 라이브 조회 없이 최신 태그를 얻는다.
 */
export const getPostTagsFromCache = async (
  databaseId: number,
): Promise<string[]> => {
  const allPosts = await getBlogNewsPostsForList(CONTENT_CATEGORY_ID.blog);
  return allPosts.find(post => post.databaseId === databaseId)?.tags ?? [];
};

/**
 * 포스트별 "마지막으로 반영된 태그 slug 배열" 캐시.
 *
 * 블로그 포스트의 태그가 바뀌면 이전·이후 태그를 모두 재생성해야 어느 한쪽에 예전 멤버가
 * 남지 않는다. 웹훅은 stateless 라 이전 값을 S3 에 별도로 기억한다(리스트 캐시는 갱신 후
 * 새 값만 남으므로 "이전 태그"를 여기서 읽는다).
 */
const getPostTagsLabel = (databaseId: number) => `getPostTags-${databaseId}`;

/** 포스트의 마지막 반영 태그 목록 조회 (없으면 빈 배열) */
export const getLastPostTags = async (
  databaseId: number,
): Promise<string[]> => {
  const data = await fetchFromCache(getPostTagsLabel(databaseId));
  return Array.isArray(data?.tags) ? data.tags : [];
};

/** 포스트의 마지막 반영 태그 목록 저장 */
export const setLastPostTags = async (
  databaseId: number,
  tags: string[],
): Promise<void> => {
  await uploadWordpressCacheDataToS3({ tags }, getPostTagsLabel(databaseId));
};

/**
 * 블로그 포스트 저장 시 재생성해야 할 태그 slug 목록을 정한다.
 * 이전 태그 ∪ 이후 태그(중복 제거) — 글에서 빠진 태그도 재생성되도록 이전 태그를 포함한다.
 */
export const tagsToRefreshForPost = (
  oldTags: string[],
  newTags: string[],
): string[] => Array.from(new Set([...oldTags, ...newTags]));
